<template>
<!-- fullstory: fs-mask keeps registrant contact pii out of recordings -->
  <div class="fr-page">
    <div class="fr-card fs-mask">
      <div class="fr-head">
        <div class="fr-logo">
          <svg viewBox="-2 0 200 128" fill="none" role="img" aria-label="Fleet Integrations">
            <g stroke="#1976D2" stroke-width="8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M36 120 C18 120 4 106 4 88 C4 74 14 62 28 57 C26 42 36 30 52 30 C58 30 64 32 68 36 C76 18 92 8 110 8 C138 8 160 30 162 57 C180 60 192 76 192 92 C192 108 178 120 162 120 Z"/>
              <path d="M65 64 H133 M116 48 L133 64 L116 80"/>
              <path d="M133 92 H65 M82 76 L65 92 L82 108"/>
            </g>
          </svg>
        </div>
        <h1 class="fr-title">Join the Bus SOS network</h1>
        <p class="fr-subtitle">Register as a yard-only provider - no telematics needed.</p>
      </div>

      <!-- receipt replaces the form once the api has emailed the request to support.
           nothing exists yet: no account, no login, no yards on the map -->
      <div v-if="result" class="fr-done">
        <div class="fr-done-head">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M8.5 12.5l2.5 2.5 4.5-5"/></svg>
          <h2>Request received</h2>
        </div>
        <p class="fr-done-line">Our team will review your registration and email login details to <strong>{{ submitted_email }}</strong>, usually within one business day.</p>

        <div v-if="result.driver_form_url" class="fr-field">
          <label class="fr-label" for="driver_link">Your driver link will be</label>
          <div class="fr-copyrow">
            <div class="fr-control"><input id="driver_link" :value="result.driver_form_url" type="text" readonly @focus="select_all" /></div>
            <button type="button" class="fr-copy" @click="copy_link">{{ copied ? 'Copied!' : 'Copy' }}</button>
          </div>
          <p class="fr-hint">It starts working once your account is created.</p>
          <p v-if="copy_failed" class="fr-hint">Copy failed - select the link and copy it manually.</p>
        </div>

        <div class="fr-done-block">
          <p class="fr-done-k">Dispatch alerts will go to</p>
          <ul class="fr-list">
            <li v-for="r in result_email_recipients" :key="'e-' + r">{{ r }}</li>
            <li v-for="r in result_sms_recipients" :key="'s-' + r">{{ format_phone(r) }} (text)</li>
            <li v-if="!result_email_recipients.length && !result_sms_recipients.length">No recipients on file - add them in Settings after you log in.</li>
          </ul>
        </div>

        <div v-if="result_yards.length" class="fr-done-block">
          <p class="fr-done-k">Yards requested</p>
          <ul class="fr-list">
            <li v-for="(y, i) in result_yards" :key="'y-' + i">{{ y }}</li>
          </ul>
        </div>

        <p class="fr-done-line">Questions, or nothing arrives? Contact <a class="fr-a" href="mailto:support@initekconsulting.com">support@initekconsulting.com</a>.</p>

        <div class="fr-done-actions">
          <router-link class="fr-submit fr-submit-link" :to="{ path: '/login' }">Back to sign in</router-link>
          <a class="fr-secondary" href="mailto:support@initekconsulting.com">Contact us</a>
        </div>
      </div>

      <template v-else>
        <div class="fr-note">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 11v5M12 8h.01"/></svg>
          <span>Free to register. Our team reviews every request and creates your account, usually within one business day.</span>
        </div>

        <section class="fr-section">
          <h2 class="fr-sec">How it works</h2>
          <ol class="fr-steps">
            <li><strong>Register your yards</strong> - once your account is set up they appear on the SOS map and in nearby-yard lookups as rescue points other companies can call.</li>
            <li><strong>Get alerted</strong> when a nearby company broadcasts an SOS - by email and text, to the dispatch contacts you choose (opt-in).</li>
            <li><strong>Raise your own SOS by dropping a pin</strong> - from your driver link or the Send SOS page.</li>
            <li><strong>Upgrade later</strong> - contact <a class="fr-a" href="mailto:support@initekconsulting.com">support@initekconsulting.com</a> to connect telematics (Samsara, Motive, Zonar, Saucon, Verizon) for live vehicle location.</li>
          </ol>
        </section>

        <div class="fr-callout">
          <p class="fr-callout-title">What yard-only means</p>
          <p>We never know where your vehicles are, so every SOS you raise starts from a pin. Nearby-vehicle matching doesn't apply to you. Yard addresses and phones become public directory information once your account is set up.</p>
        </div>

        <form @submit.prevent="register" novalidate>
          <section class="fr-section">
            <h2 class="fr-sec">Company</h2>
            <div class="fr-grid">
              <div class="fr-field">
                <label class="fr-label" for="company_name">Company name</label>
                <div class="fr-control" :class="{ 'is-error': field_errors.company_name }"><input id="company_name" v-model="form.company_name" type="text" required placeholder="Acme Coach Lines" /></div>
                <p v-if="field_errors.company_name" class="fr-msg">{{ field_errors.company_name }}</p>
                <p v-else-if="slug_preview" class="fr-hint">Your link name will be <code class="fr-code">{{ slug_preview }}</code></p>
              </div>
              <div class="fr-field">
                <label class="fr-label" for="timezone">Time zone</label>
                <div class="fr-control fr-select" :class="{ 'is-error': field_errors.timezone }">
                  <select id="timezone" v-model="form.timezone">
                    <option v-for="o in timezone_options" :key="o" :value="o">{{ o }}</option>
                  </select>
                  <svg class="fr-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>
                </div>
                <p v-if="field_errors.timezone" class="fr-msg">{{ field_errors.timezone }}</p>
              </div>
            </div>
          </section>

          <section class="fr-section">
            <h2 class="fr-sec">Primary contact</h2>
            <div class="fr-field">
              <label class="fr-label" for="contact_name">Full name</label>
              <div class="fr-control" :class="{ 'is-error': field_errors.contact_name }"><input id="contact_name" v-model="form.contact_name" type="text" required placeholder="Jane Operator" /></div>
              <p v-if="field_errors.contact_name" class="fr-msg">{{ field_errors.contact_name }}</p>
            </div>
            <div class="fr-grid">
              <div class="fr-field">
                <label class="fr-label" for="email">Email address</label>
                <div class="fr-control" :class="{ 'is-error': field_errors.email }"><input id="email" v-model="form.email" type="email" required placeholder="jane@acmecoach.com" @blur="prefill_dispatch_emails" /></div>
                <p v-if="field_errors.email" class="fr-msg">{{ field_errors.email }}</p>
              </div>
              <div class="fr-field">
                <label class="fr-label" for="phone">Phone number</label>
                <div class="fr-control" :class="{ 'is-error': field_errors.phone }"><input id="phone" v-model="form.phone" type="tel" required placeholder="(555) 555-0123" /></div>
                <p v-if="field_errors.phone" class="fr-msg">{{ field_errors.phone }}</p>
              </div>
            </div>
          </section>

          <section class="fr-section">
            <h2 class="fr-sec">Dispatch alerts</h2>
            <div class="fr-field">
              <label class="fr-label" for="dispatch_emails">Email recipients <span class="fr-opt">comma-separated, up to 5</span></label>
              <div class="fr-control" :class="{ 'is-error': field_errors.dispatch_emails }"><input id="dispatch_emails" v-model="form.dispatch_emails" type="text" placeholder="dispatch@acmecoach.com" /></div>
              <p v-if="field_errors.dispatch_emails" class="fr-msg">{{ field_errors.dispatch_emails }}</p>
            </div>
            <div class="fr-field">
              <label class="fr-label" for="dispatch_phones">SMS recipients <span class="fr-opt">comma-separated, up to 5</span></label>
              <div class="fr-control" :class="{ 'is-error': field_errors.dispatch_phones }"><input id="dispatch_phones" v-model="form.dispatch_phones" type="text" placeholder="(555) 555-0100, (555) 555-0101" /></div>
              <p v-if="field_errors.dispatch_phones" class="fr-msg">{{ field_errors.dispatch_phones }}</p>
            </div>
          </section>

          <section class="fr-section">
            <h2 class="fr-sec">Yards</h2>
            <p v-if="field_errors.yards" class="fr-msg">{{ field_errors.yards }}</p>
            <div v-for="(yard, i) in form.yards" :key="i" class="fr-yard">
              <p class="fr-yard-title">
                Yard {{ i + 1 }}
                <button v-if="i > 0" type="button" class="fr-remove" @click="removeYard(i)">Remove</button>
              </p>
              <div class="fr-grid">
                <div class="fr-field">
                  <label class="fr-label" :for="'yard_name_' + i">Yard name</label>
                  <div class="fr-control"><input :id="'yard_name_' + i" v-model="yard.yard_name" type="text" placeholder="Main yard" /></div>
                </div>
                <div class="fr-field">
                  <label class="fr-label" :for="'yard_phone_' + i">Yard phone</label>
                  <div class="fr-control" :class="{ 'is-error': field_errors['yard_' + i + '_phone'] }"><input :id="'yard_phone_' + i" v-model="yard.phone" type="tel" placeholder="(555) 555-0300" /></div>
                  <p v-if="field_errors['yard_' + i + '_phone']" class="fr-msg">{{ field_errors['yard_' + i + '_phone'] }}</p>
                </div>
              </div>
              <div class="fr-field">
                <label class="fr-label" :for="'yard_address_' + i">Yard full address</label>
                <div class="fr-control" :class="{ 'is-error': field_errors['yard_' + i + '_address'] }"><textarea :id="'yard_address_' + i" v-model="yard.address" rows="2" placeholder="123 Depot Rd, City, ST 00000"></textarea></div>
                <p v-if="field_errors['yard_' + i + '_address']" class="fr-msg">{{ field_errors['yard_' + i + '_address'] }}</p>
              </div>
            </div>
            <button v-if="can_add_yard" type="button" class="fr-add" @click="addYard">+ Add another yard</button>
            <p v-else class="fr-hint">Up to {{ max_yards }} yards here - contact support to add more.</p>
          </section>

          <section class="fr-section">
            <h2 class="fr-sec">Network</h2>
            <div class="fr-chips">
              <label class="fr-chip" :class="{ on: form.receive_from_other_companies }">
                <input type="checkbox" v-model="form.receive_from_other_companies" />
                <span>Alert me about nearby SOS</span>
              </label>
              <label class="fr-chip" :class="{ on: form.send_to_other_companies }">
                <input type="checkbox" v-model="form.send_to_other_companies" />
                <span>Let me broadcast to nearby companies</span>
              </label>
            </div>
            <p class="fr-hint">Applied when our team creates your account; you can change both later in Settings.</p>
          </section>

          <section class="fr-section">
            <h2 class="fr-sec">Anything else?</h2>
            <div class="fr-field">
              <label class="fr-label" for="vehicle_count">How many vehicles do you run? <span class="fr-opt">optional</span></label>
              <div class="fr-control fr-select">
                <select id="vehicle_count" v-model="form.vehicle_count">
                  <option value="">Select a range</option>
                  <option v-for="o in vehicle_count_options" :key="o.value" :value="o.value">{{ o.text }}</option>
                </select>
                <svg class="fr-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>
              </div>
            </div>
            <div class="fr-field">
              <label class="fr-label" for="comments">Questions or comments</label>
              <div class="fr-control"><textarea id="comments" v-model="form.comments" rows="3" placeholder="Tell us anything that would help us prepare."></textarea></div>
            </div>
          </section>

          <!-- honeypot: off-screen, bots fill it, the api quietly drops the submission -->
          <div class="fr-hp" aria-hidden="true">
            <label for="website_url">Website</label>
            <input id="website_url" v-model="form._honeypot" type="text" tabindex="-1" autocomplete="off" />
          </div>

          <transition name="fr-fade">
            <div v-if="error_message" class="fr-error">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z"/></svg>
              <span>{{ error_message }}</span>
            </div>
          </transition>

          <button id="register-button" class="fr-submit" type="submit" :disabled="submitting">
            <span v-if="submitting" class="fr-spinner" aria-hidden="true"></span>
            <span>{{ submitting ? 'Registering&hellip;' : 'Register for Bus SOS' }}</span>
          </button>
          <p class="fr-reassure">Free to register. We review every request and email your login details, usually within one business day.</p>
        </form>
      </template>

      <div class="fr-foot">
        <div class="fr-foot-links">
          <router-link class="fr-link" :to="{ path: '/login' }">Log in</router-link>
          <span class="fr-dot">&bull;</span>
          <router-link class="fr-foot-a" :to="{ path: '/register' }">Full platform registration</router-link>
          <span class="fr-dot">&bull;</span>
          <a class="fr-foot-a" target="_new" href="https://www.fleet-integrations.com">Learn more</a>
          <span class="fr-dot">&bull;</span>
          <a class="fr-foot-a" href="mailto:support@initekconsulting.com">Contact us</a>
        </div>
        <a class="fr-powered" target="_new" href="https://www.initekconsulting.com/fleet-integrations">
          <span>powered by</span><img height="18" src="/initek-logo.png" alt="Initek" />
        </a>
      </div>
    </div>
  </div>
</template>

<script>

import {register_sos_yard_only} from "../networking/apis";

const MAX_YARDS = 5
const MAX_DISPATCH = 5
// same 11 zone ids the api accepts (no "Other": pytz.timezone('') raises)
const TIMEZONES = [
  'US/Pacific', 'US/Mountain', 'US/Central', 'US/Eastern', 'America/Phoenix', 'America/New_York',
  'America/Chicago', 'America/Los_Angeles', 'America/Denver', 'America/Anchorage', 'Pacific/Honolulu',
]
const NANP_RE = /^1[2-9]\d{2}[2-9]\d{6}$/
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const GENERIC_ERROR = 'Something went wrong submitting your registration. Please try again or contact support@initekconsulting.com.'

function blank_yard() {
  return { yard_name: '', address: '', phone: '' }
}

// mirrors normalize_bus_sos_slug in util/manage_tenant_settings.py so the
// preview matches what the api will actually assign (before -2.. suffixes)
function normalize_slug(raw) {
  if (raw == null) return ''
  return String(raw).trim().toLowerCase()
    .replace(/[\s_]+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '')
}

function phone_digits(raw) {
  const d = String(raw || '').replace(/\D/g, '')
  return d.length === 10 ? '1' + d : d
}

function split_list(raw) {
  return String(raw || '').split(/[,;\s]+/).map(s => s.trim()).filter(Boolean)
}

function dedupe(list) {
  return list.filter((v, i) => list.indexOf(v) === i)
}

// phones split on separators only: "(555) 555-0101" carries its own spaces
function phone_list(raw) {
  return dedupe(String(raw || '').split(/[,;\n]+/).map(phone_digits).filter(Boolean))
}

function email_list(raw) {
  return dedupe(split_list(raw).map(s => s.toLowerCase()))
}

export default {
  name: 'sos_register',
  components: {
  },

  data() {
    return {
      submitting: false,
      error_message: null,
      result: null,
      submitted_email: '',
      copied: false,
      copy_failed: false,
      field_errors: {},
      max_yards: MAX_YARDS,
      form: {
        company_name: '',
        timezone: 'US/Eastern',
        contact_name: '',
        email: '',
        phone: '',
        dispatch_emails: '',
        dispatch_phones: '',
        yards: [blank_yard()],
        receive_from_other_companies: true,
        send_to_other_companies: true,
        vehicle_count: '',
        comments: '',
        _honeypot: '',
      },
      timezone_options: TIMEZONES,
      vehicle_count_options: [
        { text: 'Less Than 25', value: '25' },
        { text: '25 - 50', value: '25+' },
        { text: '50 - 100', value: '50+' },
        { text: '100+', value: '100+' },
      ],
    }
  },
  computed: {
    slug_preview() {
      return normalize_slug(this.form.company_name)
    },
    can_add_yard() {
      return this.form.yards.length < MAX_YARDS
    },
    result_email_recipients() {
      return (this.result && this.result.email_recipients) || []
    },
    result_sms_recipients() {
      return (this.result && this.result.sms_recipients) || []
    },
    result_yards() {
      return (this.result && this.result.yards) || []
    },
  },
  methods: {
    addYard() {
      if (this.form.yards.length >= MAX_YARDS) return false
      const new_index = this.form.yards.length
      this.form.yards.push(blank_yard())
      // yard errors are keyed by row index, so a row removed earlier could have
      // left keys for this index behind; drop only those (no validate() here, it
      // would flag the fresh blank row's address before the user touched it).
      // new object rather than delete: delete is not reactive in vue 2
      const stale = ['yard_' + new_index + '_address', 'yard_' + new_index + '_phone']
      const errors = {}
      Object.keys(this.field_errors).forEach(k => { if (!stale.includes(k)) errors[k] = this.field_errors[k] })
      this.field_errors = errors
      return true
    },
    removeYard(i) {
      // the first row is always kept: the api needs at least one yard
      if (this.form.yards.length <= 1 || i < 1 || i >= this.form.yards.length) return false
      this.form.yards.splice(i, 1)
      // yard errors are keyed by row index; after the splice the rows above i
      // shift down, so re-derive them (only once a submit has produced errors,
      // otherwise an untouched form would light up)
      if (Object.keys(this.field_errors).length) this.validate()
      return true
    },
    phone_digits,
    normalize_slug,
    format_phone(digits) {
      const d = String(digits || '')
      if (d.length !== 11) return d
      return '+' + d[0] + ' ' + d.slice(1, 4) + '-' + d.slice(4, 7) + '-' + d.slice(7)
    },
    prefill_dispatch_emails() {
      if (!String(this.form.dispatch_emails || '').trim() && String(this.form.email || '').trim())
        this.form.dispatch_emails = this.form.email.trim().toLowerCase()
    },
    // module-level helpers so validate/build_payload work on a bare `this`
    build_payload() {
      const f = this.form
      const yards = (f.yards || []).map(y => {
        const yard = {
          yard_name: String(y.yard_name || '').trim(),
          address: String(y.address || '').trim().replace(/\s+/g, ' '),
        }
        const phone = phone_digits(y.phone)
        if (phone) yard.phone = phone
        return yard
      })
      return {
        company_name: String(f.company_name || '').trim(),
        contact_name: String(f.contact_name || '').trim(),
        email: String(f.email || '').trim().toLowerCase(),
        phone: phone_digits(f.phone),
        timezone: f.timezone,
        dispatch_emails: email_list(f.dispatch_emails).join(','),
        dispatch_phones: phone_list(f.dispatch_phones).join(','),
        yards,
        receive_from_other_companies: !!f.receive_from_other_companies,
        send_to_other_companies: !!f.send_to_other_companies,
        vehicle_count: f.vehicle_count || '',
        comments: String(f.comments || '').trim(),
        _honeypot: f._honeypot || '',
      }
    },
    // client-side mirror of the api rules so most mistakes are caught before
    // the request (the api still re-validates everything)
    validate() {
      const f = this.form
      const errors = {}
      const company = String(f.company_name || '').trim()
      if (company.length < 2 || company.length > 80) errors.company_name = 'Enter your company name (2-80 characters).'
      if (!String(f.contact_name || '').trim()) errors.contact_name = "Enter the primary contact's full name."
      if (!EMAIL_RE.test(String(f.email || '').trim())) errors.email = 'Enter a valid email address.'
      if (!NANP_RE.test(phone_digits(f.phone))) errors.phone = 'Enter a 10-digit US or Canadian phone number.'
      if (!TIMEZONES.includes(f.timezone)) errors.timezone = 'Select a time zone.'

      const d_emails = email_list(f.dispatch_emails)
      if (d_emails.length > MAX_DISPATCH) errors.dispatch_emails = 'Up to ' + MAX_DISPATCH + ' dispatch email addresses.'
      else if (d_emails.some(e => !EMAIL_RE.test(e))) errors.dispatch_emails = 'One or more dispatch email addresses look invalid.'

      const d_phones = phone_list(f.dispatch_phones)
      if (d_phones.length > MAX_DISPATCH) errors.dispatch_phones = 'Up to ' + MAX_DISPATCH + ' dispatch phone numbers.'
      else if (d_phones.some(p => !NANP_RE.test(p))) errors.dispatch_phones = 'One or more dispatch phone numbers look invalid - use 10 digits each.'

      const yards = f.yards || []
      if (yards.length < 1) errors.yards = 'Add at least one yard.'
      else if (yards.length > MAX_YARDS) errors.yards = 'You can register up to ' + MAX_YARDS + ' yards here - contact support to add more.'
      yards.forEach((y, i) => {
        if (!String(y.address || '').trim()) errors['yard_' + i + '_address'] = "Enter the yard's full address."
        const p = phone_digits(y.phone)
        if (p && !NANP_RE.test(p)) errors['yard_' + i + '_phone'] = 'Enter a 10-digit phone number, or leave it blank.'
      })

      this.field_errors = errors
      return Object.keys(errors).length === 0
    },
    // a real receipt carries the slug the request was filed under; the honeypot
    // path answers 200 with {submitted: true} alone and sends nothing, so the
    // page treats that as a failure rather than promising a review
    accept_receipt(data) {
      return !!(data && data.submitted === true && data.slug)
    },
    // the api's own message (400 duplicate email/company, 429 rate limit)
    // beats the generic fallback: the user can act on "already registered"
    error_message_from(e) {
      const data = e && e.response && e.response.data
      const msg = data && (data.message || (data.data && data.data.error))
      return msg ? String(msg) : GENERIC_ERROR
    },
    async register() {
      this.error_message = null
      if (!this.validate()) {
        this.error_message = 'Check the highlighted fields and try again.'
        return
      }
      this.submitting = true
      const payload = this.build_payload()
      try {
        const { value } = await register_sos_yard_only(payload)
        const data = (value && value.data) || {}
        if (!this.accept_receipt(data)) {
          this.error_message = GENERIC_ERROR
          return
        }
        this.submitted_email = payload.email
        this.result = data
      } catch (e) {
        this.error_message = this.error_message_from(e)
      } finally {
        this.submitting = false
      }
    },
    select_all(ev) {
      if (ev && ev.target && ev.target.select) ev.target.select()
    },
    copy_link() {
      if (!this.result || !this.result.driver_form_url) return
      this.copy_failed = false
      try {
        navigator.clipboard.writeText(this.result.driver_form_url)
        this.copied = true
        setTimeout(() => { this.copied = false }, 2000)
      } catch (e) {
        this.copy_failed = true
      }
    },
  }
}
</script>

<style scoped>
/* sos register, a sibling of direct_register's design language */
.fr-page {
  min-height: 100vh;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 44px 16px;
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  color: #0a0d14;
  background:
    radial-gradient(900px 500px at 50% -8%, rgba(25,118,210,0.12), transparent 60%),
    radial-gradient(700px 500px at 100% 110%, rgba(25,118,210,0.07), transparent 55%),
    linear-gradient(180deg, #f7f9fc 0%, #eef2f7 100%);
}
.fr-card {
  width: 100%;
  max-width: 640px;
  background: #fff;
  border: 1px solid rgba(16,24,40,0.06);
  border-radius: 18px;
  box-shadow: 0 18px 50px rgba(16,24,40,0.10), 0 2px 6px rgba(16,24,40,0.04);
  padding: 38px 40px 30px;
  box-sizing: border-box;
  text-align: left;
}
.fr-head { text-align: center; margin-bottom: 20px; }
.fr-logo { width: 58px; margin: 0 auto 14px; }
.fr-logo svg { width: 100%; height: auto; display: block; }
.fr-title { margin: 0; font-size: 24px; font-weight: 700; letter-spacing: -0.01em; color: #1976D2; }
.fr-subtitle { margin: 5px 0 0; font-size: 15px; color: #5b6470; }

.fr-note {
  display: flex; align-items: center; gap: 10px;
  background: var(--blue-soft, rgba(25,118,210,0.08));
  border: 1px solid rgba(25,118,210,0.16);
  color: #1f4e7a;
  border-radius: 11px;
  padding: 12px 14px;
  font-size: 13.5px;
  line-height: 1.4;
  margin-bottom: 26px;
}
.fr-note svg { width: 19px; height: 19px; flex: none; color: #1976D2; }

.fr-section { margin-bottom: 24px; }
.fr-sec {
  font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.07em;
  color: #1976D2; margin: 0 0 14px; padding-bottom: 9px; border-bottom: 1px solid #eef1f5;
}
.fr-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.fr-field { display: flex; flex-direction: column; gap: 7px; margin-bottom: 14px; }
.fr-grid .fr-field { margin-bottom: 0; }
.fr-field:last-child { margin-bottom: 0; }
.fr-label { font-size: 13px; font-weight: 600; color: #3a4250; }
.fr-opt { font-weight: 500; color: #98a2b3; }

.fr-steps { margin: 0; padding-left: 22px; font-size: 14px; line-height: 1.5; color: #3a4250; }
.fr-steps li { margin-bottom: 9px; padding-left: 4px; }
.fr-steps li::marker { color: #1976D2; font-weight: 700; }
.fr-steps strong { color: #0a0d14; }
.fr-a { color: #1976D2; text-decoration: none; font-weight: 600; }
.fr-a:hover { color: #1565C0; text-decoration: underline; }

.fr-callout {
  margin-bottom: 26px; padding: 14px 16px; border-radius: 12px;
  background: #fff8e6; border: 1px solid #f3dfa4; color: #5a4a12; font-size: 13.5px; line-height: 1.45;
}
.fr-callout p { margin: 0; }
.fr-callout-title { font-weight: 700; margin-bottom: 4px !important; color: #7a5b00; }

.fr-control {
  display: flex; align-items: center; position: relative;
  background: #f5f7fa; border: 1.5px solid #e3e8ef; border-radius: 11px; padding: 0 14px;
  transition: border-color .15s ease, box-shadow .15s ease, background .15s ease;
}
.fr-control:focus-within { background: #fff; border-color: #1976D2; box-shadow: 0 0 0 4px rgba(25,118,210,0.12); }
.fr-control.is-error { border-color: #e0796f; box-shadow: 0 0 0 4px rgba(192,57,43,0.10); }
.fr-control input, .fr-control select, .fr-control textarea {
  flex: 1 1 auto; min-width: 0; border: 0; outline: 0; background: transparent;
  /* 16px minimum: ios safari auto-zooms focused inputs below 16px */
  font-size: 16px; color: #0a0d14; font-family: inherit; padding: 12px 0;
}
.fr-control textarea { resize: vertical; line-height: 1.45; }
.fr-control input::placeholder, .fr-control textarea::placeholder { color: #a4abb8; }
.fr-select select { appearance: none; -webkit-appearance: none; padding-right: 22px; cursor: pointer; }
.fr-chev { position: absolute; right: 14px; width: 17px; height: 17px; color: #98a2b3; pointer-events: none; }

.fr-msg { margin: 0; font-size: 12.5px; color: #b5372a; }
.fr-hint { margin: 0; font-size: 12.5px; color: #5b6470; }
.fr-code { font-family: SFMono-Regular, Menlo, Consolas, monospace; font-size: 12.5px; background: #f1f4f8; padding: 1px 6px; border-radius: 6px; color: #1f4e7a; }

.fr-chips { display: flex; flex-wrap: wrap; gap: 9px; }
.fr-chip {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 9px 14px; border-radius: 999px; cursor: pointer; user-select: none;
  background: #f5f7fa; border: 1.5px solid #e3e8ef;
  font-size: 13.5px; font-weight: 600; color: #3a4250;
  transition: all .14s ease;
}
.fr-chip input { position: absolute; opacity: 0; width: 0; height: 0; }
.fr-chip span { position: relative; }
.fr-chip span::before {
  content: ''; display: inline-block; width: 7px; height: 7px; border-radius: 50%;
  background: #c2c9d4; margin-right: 8px; vertical-align: middle; transition: background .14s ease;
}
.fr-chip:hover { border-color: #c8d2e0; }
.fr-chip.on { background: var(--blue-soft, rgba(25,118,210,0.10)); border-color: #1976D2; color: #1565C0; }
.fr-chip.on span::before { background: #1976D2; }
.fr-chips + .fr-hint { margin-top: 8px; }

.fr-yard {
  margin-bottom: 12px; padding: 16px; border-radius: 12px;
  background: #f8fafc; border: 1px dashed #cdd7e3;
}
.fr-yard .fr-grid { margin-bottom: 14px; }
.fr-yard-title { margin: 0 0 12px; font-size: 13.5px; font-weight: 650; color: #3a4250; display: flex; align-items: center; justify-content: space-between; }
.fr-remove, .fr-add {
  border: 1.5px solid #e3e8ef; background: #fff; color: #3a4250; border-radius: 999px;
  font-size: 12.5px; font-weight: 600; padding: 5px 12px; cursor: pointer; font-family: inherit;
}
.fr-remove:hover { border-color: #e0796f; color: #b5372a; }
.fr-add { color: #1976D2; border-color: #1976D2; background: #fff; padding: 8px 16px; font-size: 13.5px; }
.fr-add:hover { background: var(--blue-soft, rgba(25,118,210,0.08)); }

.fr-hp { position: absolute; left: -9999px; top: auto; width: 1px; height: 1px; overflow: hidden; }

.fr-error {
  display: flex; align-items: center; gap: 9px; margin-bottom: 14px;
  padding: 11px 13px; background: #fdecea; color: #b5372a; border-radius: 10px; font-size: 13.5px; line-height: 1.35;
}
.fr-error svg { width: 18px; height: 18px; flex: none; }

.fr-submit {
  width: 100%; height: 50px; margin-top: 6px; border: 0; border-radius: 11px;
  background: linear-gradient(180deg, #1f86e6 0%, #1565C0 100%);
  color: #fff; font-size: 15.5px; font-weight: 650; letter-spacing: 0.01em; cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: 10px;
  box-shadow: 0 8px 18px rgba(25,118,210,0.28);
  transition: filter .15s ease, transform .04s ease, box-shadow .15s ease;
}
.fr-submit:hover:not(:disabled) { filter: brightness(1.05); }
.fr-submit:active:not(:disabled) { transform: translateY(1px); }
.fr-submit:disabled { opacity: 0.65; cursor: default; box-shadow: none; }
.fr-submit-link { text-decoration: none; box-sizing: border-box; }
.fr-submit-link:hover { color: #fff; }
.fr-spinner { width: 17px; height: 17px; border: 2.5px solid rgba(255,255,255,0.45); border-top-color: #fff; border-radius: 50%; animation: fr-spin 0.7s linear infinite; }
@keyframes fr-spin { to { transform: rotate(360deg); } }

.fr-reassure { margin: 13px 0 0; text-align: center; font-size: 13px; color: #5b6470; }

/* success panel */
.fr-done-head { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; color: #1b7f4b; }
.fr-done-head svg { width: 26px; height: 26px; flex: none; }
.fr-done-head h2 { margin: 0; font-size: 20px; font-weight: 700; }
.fr-done-line { margin: 0 0 12px; font-size: 14px; line-height: 1.5; color: #3a4250; }
.fr-done-k { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.07em; color: #1976D2; margin: 0 6px 4px 0; }
.fr-done-block { margin-bottom: 12px; }
.fr-list { margin: 0; padding-left: 20px; font-size: 14px; line-height: 1.5; color: #3a4250; }
.fr-copyrow { display: flex; gap: 8px; align-items: stretch; }
.fr-copyrow .fr-control { flex: 1 1 auto; }
.fr-copy {
  flex: none; border: 1.5px solid #1976D2; background: #fff; color: #1976D2; border-radius: 11px;
  font-size: 13.5px; font-weight: 600; padding: 0 16px; cursor: pointer; font-family: inherit;
}
.fr-copy:hover { background: var(--blue-soft, rgba(25,118,210,0.08)); }
.fr-done .fr-field { margin-bottom: 14px; }
.fr-done-actions { display: flex; flex-direction: column; gap: 10px; margin-top: 6px; }
.fr-secondary {
  display: flex; align-items: center; justify-content: center; height: 46px; border-radius: 11px;
  border: 1.5px solid #e3e8ef; color: #1976D2; font-size: 14.5px; font-weight: 600; text-decoration: none;
}
.fr-secondary:hover { border-color: #1976D2; color: #1565C0; }

.fr-foot {
  margin-top: 24px; padding-top: 18px; border-top: 1px solid #eef1f5;
  display: flex; flex-direction: column; align-items: center; gap: 12px;
}
.fr-foot-links { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; justify-content: center; }
.fr-link, .fr-foot-a { color: #5b6470; font-size: 13px; font-weight: 500; text-decoration: none; }
.fr-link { color: #1976D2; font-weight: 600; }
.fr-foot-a:hover, .fr-link:hover { color: #1565C0; }
.fr-dot { color: #c2c9d4; font-size: 12px; }
.fr-powered { display: inline-flex; align-items: center; gap: 7px; font-size: 12px; color: #98a2b3; text-decoration: none; }
.fr-powered img { opacity: 0.9; }

.fr-fade-enter-active, .fr-fade-leave-active { transition: opacity .2s ease; }
.fr-fade-enter, .fr-fade-leave-to { opacity: 0; }

@media (max-width: 560px) {
  .fr-card { padding: 30px 22px 24px; }
  .fr-grid { grid-template-columns: 1fr; gap: 0; }
  .fr-grid .fr-field { margin-bottom: 14px; }
  .fr-yard .fr-grid { margin-bottom: 0; }
}
</style>
